import { ArrowUpRight, EnvelopeSimple, Phone } from '@phosphor-icons/react';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from '../../schemas';
import { PLATFORM_META } from '../../linkPlatforms';
import type { TemplateProps } from './shared';

const BG = '#F7F7FB';
const RED = '#E4000F';
const RED_DEEP = '#B30000';
const NAVY = '#0C2340';
const NAVY_SOFT = '#16325C';
const MUTED = '#4B5563';

const WEB_PATTERN =
  'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\' viewBox=\'0 0 160 160\'><g fill=\'none\' stroke=\'%23ffffff\' stroke-opacity=\'0.18\' stroke-width=\'1\'><circle cx=\'80\' cy=\'80\' r=\'20\'/><circle cx=\'80\' cy=\'80\' r=\'40\'/><circle cx=\'80\' cy=\'80\' r=\'60\'/><circle cx=\'80\' cy=\'80\' r=\'80\'/><line x1=\'80\' y1=\'0\' x2=\'80\' y2=\'160\'/><line x1=\'0\' y1=\'80\' x2=\'160\' y2=\'80\'/><line x1=\'23\' y1=\'23\' x2=\'137\' y2=\'137\'/><line x1=\'137\' y1=\'23\' x2=\'23\' y2=\'137\'/></g></svg>")';

export function SpidermanTemplate({ draft, enabled, ownerName }: TemplateProps) {
  return (
    <div className="min-h-full font-sans" style={{ background: BG, color: NAVY }}>
      <div className="mx-auto max-w-4xl px-8 py-12">
        <header
          className="flex items-center justify-between border-b-2 pb-4 text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ borderColor: NAVY, color: NAVY }}
        >
          <div className="flex items-center gap-2.5">
            <SpiderSigil />
            <span>{ownerName}</span>
          </div>
          <span style={{ color: RED }}>⬢ {new Date().getFullYear()}</span>
        </header>

        {enabled.has('hero') && draft.hero ? <HeroBlock values={draft.hero} /> : null}
        {enabled.has('about') && draft.about ? <AboutBlock values={draft.about} /> : null}
        {enabled.has('projects') && draft.projects ? (
          <ProjectsBlock values={draft.projects} />
        ) : null}
        {enabled.has('experience') && draft.experience ? (
          <ExperienceBlock values={draft.experience} />
        ) : null}
        {enabled.has('education') && draft.education ? (
          <EducationBlock values={draft.education} />
        ) : null}
        {enabled.has('contact') && draft.contact ? (
          <ContactBlock values={draft.contact} />
        ) : null}

        <footer
          className="mt-20 flex items-center justify-center gap-3 border-t-2 pt-5 text-[11px] font-bold uppercase tracking-[0.2em]"
          style={{ borderColor: NAVY, color: NAVY }}
        >
          <span style={{ color: RED }}>◂</span>
          With great portfolio comes great responsibility.
          <span style={{ color: RED }}>▸</span>
        </footer>
      </div>
    </div>
  );
}

function SpiderSigil() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" fill={RED} />
      <path
        d="M12 6v12M6 12h12M7.5 7.5l9 9M16.5 7.5l-9 9"
        stroke={NAVY}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="3" fill={NAVY} />
    </svg>
  );
}

function SectionHeading({ num, title }: { num: string; title: string }) {
  return (
    <div className="mt-16 mb-6 flex items-center gap-3">
      <span
        className="inline-flex items-center justify-center rounded-md px-2.5 py-1 font-mono text-[11px] font-bold tracking-[0.2em] text-white"
        style={{ background: RED }}
      >
        {num}
      </span>
      <h2 className="font-display text-3xl font-black tracking-tight" style={{ color: NAVY }}>
        {title}
      </h2>
      <span className="h-[2px] flex-1 rounded-full" style={{ background: NAVY, opacity: 0.15 }} />
    </div>
  );
}

function HeroBlock({ values }: { values: HeroValues }) {
  const availabilityLabel: Record<HeroValues['availability'], string> = {
    available: 'Swinging into work',
    limited: 'Selective missions',
    closed: 'On patrol elsewhere',
  };
  return (
    <section className="pt-10">
      <div
        className="relative overflow-hidden rounded-2xl px-8 py-12"
        style={{ background: RED, backgroundImage: WEB_PATTERN, color: 'white' }}
      >
        {values.tagline ? (
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-white/80">
            ⬢ {values.tagline}
          </p>
        ) : null}
        <h1 className="mt-3 font-display text-5xl font-black uppercase leading-[0.95] tracking-tight">
          {values.headline}
        </h1>
        {values.subheadline ? (
          <p className="mt-5 max-w-[58ch] text-lg font-medium leading-relaxed text-white/90">
            {values.subheadline}
          </p>
        ) : null}
        <div className="mt-7 flex flex-wrap items-center gap-3">
          {values.ctaLabel && values.ctaHref ? (
            <a
              href={values.ctaHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold transition-transform hover:-translate-y-0.5"
              style={{ background: NAVY, color: 'white' }}
            >
              {values.ctaLabel}
              <ArrowUpRight size={12} weight="bold" />
            </a>
          ) : null}
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-white/40 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white"
          >
            <span
              className={`h-1.5 w-1.5 rounded-full bg-white ${
                values.availability === 'available' ? 'animate-pulse' : ''
              }`}
            />
            {availabilityLabel[values.availability]}
          </span>
        </div>
      </div>
    </section>
  );
}

function AboutBlock({ values }: { values: AboutValues }) {
  return (
    <section>
      <SectionHeading num="01" title="Origin story" />
      <p
        className="max-w-[62ch] whitespace-pre-line text-base leading-relaxed"
        style={{ color: NAVY_SOFT }}
      >
        {values.body}
      </p>
      {values.skills.length ? (
        <div className="mt-6">
          <p
            className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: RED }}
          >
            Powers &amp; abilities
          </p>
          <div className="flex flex-wrap gap-1.5">
            {values.skills.map((s) => (
              <span
                key={s}
                className="rounded-md border-2 px-2.5 py-1 text-xs font-bold"
                style={{ borderColor: NAVY, color: NAVY, background: 'white' }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {values.resumeUrl ? (
        <a
          href={values.resumeUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-bold text-white"
          style={{ background: RED }}
        >
          Download the résumé <ArrowUpRight size={12} weight="bold" />
        </a>
      ) : null}
    </section>
  );
}

function ProjectsBlock({ values }: { values: ProjectsValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="02" title="Adventures" />
      <div className="space-y-6">
        {values.items.map((p) => (
          <article
            key={p.id}
            className="flex flex-col overflow-hidden rounded-xl border-2 bg-white transition-transform hover:-translate-y-0.5"
            style={{ borderColor: NAVY }}
          >
            {p.mediaKind !== 'none' && p.mediaUrl ? (
              <div className="border-b-2" style={{ borderColor: NAVY }}>
                {p.mediaKind === 'image' ? (
                  <img
                    src={p.mediaUrl}
                    alt={p.mediaAlt ?? p.title}
                    className="aspect-[16/10] w-full object-cover"
                  />
                ) : (
                  <div
                    className="grid aspect-[16/10] w-full place-items-center text-[11px] font-bold uppercase tracking-[0.22em] text-white"
                    style={{ background: NAVY }}
                  >
                    ▶ {p.mediaUrl}
                  </div>
                )}
              </div>
            ) : null}
            <div className="flex flex-1 flex-col p-5">
              <div
                className="flex items-center justify-between font-mono text-[11px] font-bold uppercase tracking-[0.2em]"
                style={{ color: RED }}
              >
                <span>{[p.role, p.client].filter(Boolean).join(' · ') || 'Mission'}</span>
                <span>{p.year}</span>
              </div>
              <h3
                className="mt-1.5 font-display text-xl font-black tracking-tight"
                style={{ color: NAVY }}
              >
                {p.title}
              </h3>
              {p.summary ? (
                <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>
                  {p.summary}
                </p>
              ) : null}
              {p.bullets && p.bullets.length ? (
                <ul className="mt-3 flex-1 space-y-1.5 text-sm" style={{ color: MUTED }}>
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span style={{ color: RED }}>▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {p.tags && p.tags.length ? (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded px-1.5 py-0.5 text-[10px] font-bold"
                      style={{ background: BG, color: NAVY_SOFT }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
              {p.url ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-bold"
                  style={{ color: RED }}
                >
                  Read the story <ArrowUpRight size={12} weight="bold" />
                </a>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ExperienceBlock({ values }: { values: ExperienceValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="03" title="Team-ups" />
      <div className="space-y-4">
        {values.items.map((e) => (
          <div
            key={e.id}
            className="rounded-xl border-2 bg-white p-5"
            style={{ borderColor: NAVY }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-display text-lg font-black tracking-tight" style={{ color: NAVY }}>
                {e.role}
              </h3>
              <span
                className="rounded-md px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white"
                style={{ background: RED }}
              >
                {e.start} — {e.current ? 'Now' : e.end}
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold" style={{ color: NAVY_SOFT }}>
              {e.company}
              {e.location ? ` · ${e.location}` : ''}
            </p>
            {e.summary ? (
              <p className="mt-3 max-w-[62ch] text-sm leading-relaxed" style={{ color: MUTED }}>
                {e.summary}
              </p>
            ) : null}
            {e.highlights && e.highlights.length ? (
              <ul className="mt-3 space-y-1.5 text-sm" style={{ color: MUTED }}>
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: RED }}>▸</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function EducationBlock({ values }: { values: EducationValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="04" title="Academy" />
      <div className="space-y-4">
        {values.items.map((e) => (
          <div
            key={e.id}
            className="rounded-xl border-2 bg-white p-5"
            style={{ borderColor: NAVY }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-display text-lg font-black tracking-tight" style={{ color: NAVY }}>
                {e.institution}
              </h3>
              <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>
                {e.startYear}
                {e.endYear ? ` – ${e.endYear}` : ''}
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold" style={{ color: NAVY_SOFT }}>
              {e.credential}
              {e.gpa ? ` · GPA ${e.gpa}` : ''}
            </p>
            {e.note ? (
              <p className="mt-2 max-w-[60ch] text-sm" style={{ color: MUTED }}>
                {e.note}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactBlock({ values }: { values: ContactValues }) {
  return (
    <section>
      <SectionHeading num="05" title="Spider-signal" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {values.email ? (
          <a
            href={`mailto:${values.email}`}
            className="flex items-center justify-between rounded-xl border-2 bg-white px-4 py-3 text-sm font-bold transition-colors hover:bg-red-50"
            style={{ borderColor: NAVY, color: NAVY }}
          >
            <span className="inline-flex items-center gap-2">
              <EnvelopeSimple size={14} style={{ color: RED }} />
              {values.email}
            </span>
            <ArrowUpRight size={12} weight="bold" style={{ color: RED }} />
          </a>
        ) : null}
        {values.phone ? (
          <a
            href={`tel:${values.phone.replace(/\s+/g, '')}`}
            className="flex items-center justify-between rounded-xl border-2 bg-white px-4 py-3 text-sm font-bold transition-colors hover:bg-red-50"
            style={{ borderColor: NAVY, color: NAVY }}
          >
            <span className="inline-flex items-center gap-2">
              <Phone size={14} style={{ color: RED }} />
              {values.phone}
            </span>
            <ArrowUpRight size={12} weight="bold" style={{ color: RED }} />
          </a>
        ) : null}
        {values.links.map((l) => {
          const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
          const Icon = meta.icon;
          const href =
            l.platform === 'email' && !l.href.startsWith('mailto:') ? `mailto:${l.href}` : l.href;
          return (
            <a
              key={l.id}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-xl border-2 bg-white px-4 py-3 text-sm font-bold"
              style={{ borderColor: NAVY, color: NAVY }}
            >
              <span className="inline-flex items-center gap-2">
                <Icon size={14} weight="regular" style={{ color: RED }} />
                {l.label || meta.label}
              </span>
              <ArrowUpRight size={12} weight="bold" style={{ color: RED }} />
            </a>
          );
        })}
      </div>
      {values.location ? (
        <p
          className="mt-5 font-mono text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ color: RED_DEEP }}
        >
          Patrol · {values.location}
        </p>
      ) : null}
      {values.allowInquiryForm ? (
        <div
          className="mt-6 rounded-xl border-2 border-dashed p-5"
          style={{ borderColor: RED, background: 'white' }}
        >
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: RED }}>
            ⬢ Send a signal
          </p>
          <p className="mt-2 text-sm" style={{ color: MUTED }}>
            A short inquiry form renders here on your live site.
          </p>
        </div>
      ) : null}
    </section>
  );
}
